import React, { useState, useEffect, useRef, useMemo } from "react";
import CommissionService from "../services/CommissionService";
import { detailedReportsPaymentStatus } from "../config/config";
import Loader from "./Loader";
import { toast } from "react-toastify";
import {
  FiCalendar,
  FiDollarSign,
  FiTrendingUp,
  FiFileText,
  FiUser,
  FiUpload,
  FiDownload,
  FiMapPin,
  FiChevronUp,
  FiChevronDown,
  FiColumns,
} from "react-icons/fi";
import { endOfMonth, format } from "date-fns";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { getISTTodayYmd, getISTFirstOfMonthYmd } from "../utils/istCalendarDate";
import { amountToWordsInr } from "../utils/amountToWordsInr";
import { STATE_UT_OPTIONS, getStateUtDisplayLabel } from "../constants/stateUtOptions";
import { FaSearch, FaArrowUp, FaArrowDown } from "react-icons/fa";
import ReportDateRangeFilters from "./ReportDateRangeFilters";
import ReportCompareFilters from "./ReportCompareFilters";
import {
  computeComparisonRange,
  formatBreakdownDateRangeLabel,
  calcPercentChange,
} from "../utils/rideBreakdownComparison";

/** Toggleable columns in detailed commission table (Driver / Status / Date always shown). */
const DETAILED_COL_BEFORE = [
  "rideDetails",
  "rideFare",
  "driverEarnings",
  "commissionPct",
  "commission",
  "gstBefore",
];
const DETAILED_COL_AFTER = [
  "totalDue",
  "ids",
  "settlementAmount",
  "commissionAfter",
  "gstAfter",
];

const DEFAULT_DETAILED_COLUMN_VISIBILITY = Object.fromEntries(
  [...DETAILED_COL_BEFORE, ...DETAILED_COL_AFTER].map((k) => [k, true])
);

const DETAILED_TOTALS_CARD_ORDER = ["PAID", "PENDING", "FAILED"];

const DETAILED_COLUMN_LABELS = {
  rideDetails: "Ride Details",
  rideFare: "Ride Amount",
  driverEarnings: "Driver Earnings",
  commissionPct: "Commission %",
  commission: "Commission",
  gstBefore: "GST (18%)",
  totalDue: "Total Due",
  ids: "IDs",
  settlementAmount: "Settlement Amount",
  commissionAfter: "Commission",
  gstAfter: "GST (18%)",
};

function sortDetailedTotalsEntries(totals) {
  if (!totals || typeof totals !== "object") return [];
  return Object.entries(totals).sort(([a], [b]) => {
    const orderA = DETAILED_TOTALS_CARD_ORDER.indexOf(a);
    const orderB = DETAILED_TOTALS_CARD_ORDER.indexOf(b);
    return (orderA === -1 ? 99 : orderA) - (orderB === -1 ? 99 : orderB);
  });
}

function getCommissionRefId(refOrDoc) {
  if (refOrDoc == null) return null;
  if (typeof refOrDoc === "object" && refOrDoc._id != null) return refOrDoc._id;
  return refOrDoc;
}

function formatIdCellPart(value) {
  if (value == null) return "—";
  const s = String(value).trim();
  return s !== "" ? s : "—";
}

/** Empty merchant / PhonePe settlement ids use hyphen per QA plan (driver/ride keep em dash). */
function formatMerchantOrTxnIdEmpty(value) {
  if (value == null) return "-";
  const s = String(value).trim();
  return s !== "" ? s : "-";
}

function getSummaryRowSortValue(row, key) {
  switch (key) {
    case "date":
      return row.dateTs || 0;
    case "status":
      return row.statusSort;
    case "totalAmount":
      return Number(row.totalAmount) || 0;
    case "totalCommission":
      return Number(row.totalCommission) || 0;
    case "totalGst":
      return Number(row.totalGst) || 0;
    case "totalRideAmount":
      return Number(row.totalRideAmount) || 0;
    case "count":
      return Number(row.count) || 0;
    default:
      return "";
  }
}

function compareSummaryRows(a, b, key, dir) {
  const va = getSummaryRowSortValue(a, key);
  const vb = getSummaryRowSortValue(b, key);
  const mult = dir === "desc" ? -1 : 1;
  const isNullish = (x) =>
    x === null || x === undefined || (typeof x === "number" && Number.isNaN(x));
  if (isNullish(va) && isNullish(vb)) return 0;
  if (isNullish(va)) return 1;
  if (isNullish(vb)) return -1;
  if (typeof va === "number" && typeof vb === "number") {
    return (va - vb) * mult;
  }
  return String(va).localeCompare(String(vb), "en") * mult;
}

function comparisonDeltaColorClass(direction) {
  if (direction === "down") return "text-red-600";
  if (direction === "up") return "text-green-600";
  return "text-gray-500";
}

function formatSignedComparisonDiff(current, previous, formatValue = (v) => v) {
  const diff = (Number(current) || 0) - (Number(previous) || 0);
  if (diff === 0) return formatValue(0);
  if (diff > 0) {
    const formatted = formatValue(diff);
    return formatted.startsWith("+") ? formatted : `+${formatted}`;
  }
  return formatValue(diff);
}

function renderComparisonDelta(
  current,
  previous,
  formatValue = (v) => v,
  variant = "block"
) {
  if (previous === null || previous === undefined) return null;
  const pct = calcPercentChange(Number(current) || 0, Number(previous) || 0);
  const diffDisplay = formatSignedComparisonDiff(current, previous, formatValue);
  const colorClass = comparisonDeltaColorClass(pct.direction);
  const wrapperClass =
    variant === "inline"
      ? "mt-0.5 text-[11px] flex flex-col items-end gap-0.5"
      : "mt-2 pt-2 border-t border-gray-100 text-xs flex items-center justify-between gap-2";

  if (variant === "inline") {
    return (
      <div className={wrapperClass}>
        <span className="text-gray-400">vs {formatValue(previous)}</span>
        <span className={`inline-flex items-center gap-1.5 font-semibold whitespace-nowrap ${colorClass}`}>
          <span>{diffDisplay}</span>
          <span className="inline-flex items-center gap-1">
            {pct.direction === "down" ? (
              <FaArrowDown className="text-[10px]" />
            ) : pct.direction === "up" ? (
              <FaArrowUp className="text-[10px]" />
            ) : null}
            <span>{pct.display}</span>
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <span className="text-gray-500">vs {formatValue(previous)}</span>
      <span className={`inline-flex items-center gap-1.5 font-semibold whitespace-nowrap ${colorClass}`}>
        <span>{diffDisplay}</span>
        <span className="inline-flex items-center gap-1">
          {pct.direction === "down" ? (
            <FaArrowDown className="text-[10px]" />
          ) : pct.direction === "up" ? (
            <FaArrowUp className="text-[10px]" />
          ) : null}
          <span>{pct.display}</span>
        </span>
      </span>
    </div>
  );
}

const STATUS_BREAKDOWN_METRICS = [
  { label: "Total Amount", key: "totalAmount" },
  { label: "Commission", key: "totalCommission" },
  { label: "GST", key: "totalGst" },
];

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount || 0);
}

function renderStatusBreakdownMetrics(totals, compareTotals, showComparison) {
  return STATUS_BREAKDOWN_METRICS.map(({ label, key }) => {
    const current = totals?.[key] || 0;
    return (
      <div key={key}>
        <div className="flex justify-between">
          <span className="text-gray-600">{label}:</span>
          <span className="font-medium">{formatCurrency(current)}</span>
        </div>
        {showComparison &&
          renderComparisonDelta(
            current,
            compareTotals?.[key] ?? null,
            formatCurrency,
            "inline"
          )}
      </div>
    );
  });
}

const CommissionReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [detailedData, setDetailedData] = useState(null);
  const [activeView, setActiveView] = useState("summary"); // 'summary' | 'detailed' | 'taxInvoice' | 'statewise'
  const [filters, setFilters] = useState({
    startDate: getISTFirstOfMonthYmd(),
    endDate: getISTTodayYmd(),
    driverSearch: "",
    stateUt: "",
  });
  const [detailedFilters, setDetailedFilters] = useState({
    startDate: "",
    endDate: "",
    driverSearch: "",
    paymentStatus: "",
    stateUt: "",
    page: 1,
    limit: 10,
    sortBy: "",
    sortOrder: "desc",
  });
  const [detailedPagination, setDetailedPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [updatingCommissionId, setUpdatingCommissionId] = useState(null);
  const [settlementFile, setSettlementFile] = useState(null);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementRows, setSettlementRows] = useState([]);
  const [settlementPagination, setSettlementPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [settlementFilters, setSettlementFilters] = useState({
    startDate: "",
    endDate: "",
    driverId: "",
    paymentOrderId: "",
    paymentTransactionId: "",
  });
  const [settlementSummary, setSettlementSummary] = useState(null);

  const [summarySort, setSummarySort] = useState({ key: null, dir: "asc" });
  const [detailedColumnVisibility, setDetailedColumnVisibility] = useState(() => ({
    ...DEFAULT_DETAILED_COLUMN_VISIBILITY,
  }));
  const [detailedColumnsMenuOpen, setDetailedColumnsMenuOpen] = useState(false);
  const detailedColumnsMenuRef = useRef(null);

  const taxInvoiceRef = useRef(null);
  const [taxInvoiceMonth, setTaxInvoiceMonth] = useState(
    () => new Date().getMonth() + 1
  );
  const [taxInvoiceYear, setTaxInvoiceYear] = useState(() =>
    new Date().getFullYear()
  );
  const [taxInvoiceApplied, setTaxInvoiceApplied] = useState(null);
  const [taxInvoiceData, setTaxInvoiceData] = useState(null);
  const [taxInvoiceLoading, setTaxInvoiceLoading] = useState(false);
  const [taxInvoicePdfLoading, setTaxInvoicePdfLoading] = useState(false);
  const [statewiseStartDate, setStatewiseStartDate] = useState(getISTFirstOfMonthYmd);
  const [statewiseEndDate, setStatewiseEndDate] = useState(getISTTodayYmd);
  const [statewiseApplied, setStatewiseApplied] = useState(null);
  const [statewiseData, setStatewiseData] = useState(null);
  const [statewiseLoading, setStatewiseLoading] = useState(false);
  const [stateUtDropdownOpen, setStateUtDropdownOpen] = useState(false);
  const [stateUtSearch, setStateUtSearch] = useState("");
  const stateUtDropdownRef = useRef(null);
  const [detailedStateUtDropdownOpen, setDetailedStateUtDropdownOpen] = useState(false);
  const [detailedStateUtSearch, setDetailedStateUtSearch] = useState("");
  const detailedStateUtDropdownRef = useRef(null);

  const [summaryCompareEnabled, setSummaryCompareEnabled] = useState(false);
  const [summaryCompareMode, setSummaryCompareMode] = useState("previous_period");
  const [summaryCompareStartDate, setSummaryCompareStartDate] = useState("");
  const [summaryCompareEndDate, setSummaryCompareEndDate] = useState("");
  const [summaryAppliedComparison, setSummaryAppliedComparison] = useState(null);
  const [summaryCompareData, setSummaryCompareData] = useState(null);
  const [summaryCompareLoading, setSummaryCompareLoading] = useState(false);
  const [summaryDateFilterApplied, setSummaryDateFilterApplied] = useState(false);
  const summaryAppliedComparisonRef = useRef(null);

  const [detailedCompareEnabled, setDetailedCompareEnabled] = useState(false);
  const [detailedCompareMode, setDetailedCompareMode] = useState("previous_period");
  const [detailedCompareStartDate, setDetailedCompareStartDate] = useState("");
  const [detailedCompareEndDate, setDetailedCompareEndDate] = useState("");
  const [detailedAppliedComparison, setDetailedAppliedComparison] = useState(null);
  const [detailedCompareData, setDetailedCompareData] = useState(null);
  const [detailedCompareLoading, setDetailedCompareLoading] = useState(false);
  const [detailedDateFilterApplied, setDetailedDateFilterApplied] = useState(false);
  const detailedAppliedComparisonRef = useRef(null);

  const taxInvoiceYearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => y - 5 + i);
  }, []);

  const MONTH_ABBR = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  // Fetch commission report
  const fetchSummaryComparison = async (comparison) => {
    if (!comparison?.startDate || !comparison?.endDate) {
      setSummaryCompareData(null);
      return;
    }
    setSummaryCompareLoading(true);
    try {
      const params = {
        startDate: comparison.startDate,
        endDate: comparison.endDate,
      };
      if (filters.driverSearch?.trim()) {
        params.driverSearch = filters.driverSearch.trim();
      }
      if (filters.stateUt) {
        params.stateUt = filters.stateUt;
      }
      const response = await CommissionService.getCommissionReport(params);
      setSummaryCompareData(response.data);
    } catch (error) {
      console.error("Error fetching summary comparison:", error);
      setSummaryCompareData(null);
    } finally {
      setSummaryCompareLoading(false);
    }
  };

  const fetchDetailedComparison = async (comparison) => {
    if (!comparison?.startDate || !comparison?.endDate) {
      setDetailedCompareData(null);
      return;
    }
    setDetailedCompareLoading(true);
    try {
      const params = {
        page: 1,
        limit: detailedFilters.limit,
        startDate: comparison.startDate,
        endDate: comparison.endDate,
      };
      if (detailedFilters.driverSearch?.trim()) {
        params.driverSearch = detailedFilters.driverSearch.trim();
      }
      if (detailedFilters.paymentStatus) {
        params.paymentStatus = detailedFilters.paymentStatus;
      }
      if (detailedFilters.stateUt) {
        params.stateUt = detailedFilters.stateUt;
      }
      const response = await CommissionService.getDetailedCommissionReport(params);
      setDetailedCompareData(response.data);
    } catch (error) {
      console.error("Error fetching detailed comparison:", error);
      setDetailedCompareData(null);
    } finally {
      setDetailedCompareLoading(false);
    }
  };

  const fetchReport = async ({ markDateFilterApplied = false } = {}) => {
    if (!filters.startDate || !filters.endDate) {
      toast.warning("Please select start and end dates");
      return;
    }

    setLoading(true);
    try {
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
      };
      if (filters.driverSearch?.trim()) {
        params.driverSearch = filters.driverSearch.trim();
      }
      if (filters.stateUt) {
        params.stateUt = filters.stateUt;
      }

      const response = await CommissionService.getCommissionReport(params);
      setReportData(response.data);
      if (markDateFilterApplied) {
        setSummaryDateFilterApplied(true);
      }
      if (summaryAppliedComparisonRef.current) {
        void fetchSummaryComparison(summaryAppliedComparisonRef.current);
      }
    } catch (error) {
      console.error("Error fetching commission report:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch detailed commission report
  const fetchDetailedReport = async (
    page = 1,
    override = null,
    { markDateFilterApplied = false } = {}
  ) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        limit: detailedFilters.limit,
      };

      if (detailedFilters.startDate) params.startDate = detailedFilters.startDate;
      if (detailedFilters.endDate) params.endDate = detailedFilters.endDate;
      if (detailedFilters.driverSearch?.trim()) {
        params.driverSearch = detailedFilters.driverSearch.trim();
      }
      if (detailedFilters.paymentStatus) params.paymentStatus = detailedFilters.paymentStatus;
      if (detailedFilters.stateUt) params.stateUt = detailedFilters.stateUt;

      const sortBy = override?.sortBy ?? detailedFilters.sortBy;
      const sortOrder = override?.sortOrder ?? detailedFilters.sortOrder;
      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder || "desc";
      }

      const response = await CommissionService.getDetailedCommissionReport(params);
      setDetailedData(response.data);
      setDetailedPagination(response.data.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0,
      });
      if (markDateFilterApplied) {
        if (detailedFilters.startDate && detailedFilters.endDate) {
          setDetailedDateFilterApplied(true);
        } else {
          setDetailedDateFilterApplied(false);
          setDetailedAppliedComparison(null);
          setDetailedCompareData(null);
        }
      }
      if (detailedAppliedComparisonRef.current) {
        void fetchDetailedComparison(detailedAppliedComparisonRef.current);
      }
    } catch (error) {
      console.error("Error fetching detailed commission report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeView === "summary") {
      fetchReport();
    } else if (activeView === "detailed") {
      fetchDetailedReport();
      fetchSettlementReport(1);
    }
  }, [activeView]);

  useEffect(() => {
    summaryAppliedComparisonRef.current = summaryAppliedComparison;
  }, [summaryAppliedComparison]);

  useEffect(() => {
    detailedAppliedComparisonRef.current = detailedAppliedComparison;
  }, [detailedAppliedComparison]);

  useEffect(() => {
    if (!summaryAppliedComparison) {
      setSummaryCompareData(null);
      return;
    }
    if (summaryDateFilterApplied) {
      void fetchSummaryComparison(summaryAppliedComparison);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryAppliedComparison]);

  useEffect(() => {
    if (!detailedAppliedComparison) {
      setDetailedCompareData(null);
      return;
    }
    if (detailedDateFilterApplied) {
      void fetchDetailedComparison(detailedAppliedComparison);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailedAppliedComparison]);

  const summaryMainStartYmd = String(filters.startDate || "").trim();
  const summaryMainEndYmd = String(filters.endDate || "").trim();
  const summaryHasMainDateRange = Boolean(summaryMainStartYmd && summaryMainEndYmd);

  const detailedMainStartYmd = String(detailedFilters.startDate || "").trim();
  const detailedMainEndYmd = String(detailedFilters.endDate || "").trim();
  const detailedHasMainDateRange = Boolean(detailedMainStartYmd && detailedMainEndYmd);

  useEffect(() => {
    if (!summaryCompareEnabled || summaryCompareMode === "none") return;
    const preview = computeComparisonRange(
      summaryCompareMode,
      summaryMainStartYmd,
      summaryMainEndYmd,
      summaryCompareStartDate,
      summaryCompareEndDate
    );
    if (preview && summaryCompareMode !== "custom") {
      setSummaryCompareStartDate(preview.startDate);
      setSummaryCompareEndDate(preview.endDate);
    }
  }, [summaryCompareEnabled, summaryCompareMode, summaryMainStartYmd, summaryMainEndYmd]);

  useEffect(() => {
    if (!detailedCompareEnabled || detailedCompareMode === "none") return;
    const preview = computeComparisonRange(
      detailedCompareMode,
      detailedMainStartYmd,
      detailedMainEndYmd,
      detailedCompareStartDate,
      detailedCompareEndDate
    );
    if (preview && detailedCompareMode !== "custom") {
      setDetailedCompareStartDate(preview.startDate);
      setDetailedCompareEndDate(preview.endDate);
    }
  }, [detailedCompareEnabled, detailedCompareMode, detailedMainStartYmd, detailedMainEndYmd]);

  useEffect(() => {
    const onDoc = (e) => {
      if (stateUtDropdownRef.current && !stateUtDropdownRef.current.contains(e.target)) {
        setStateUtDropdownOpen(false);
      }
    };
    if (stateUtDropdownOpen) {
      document.addEventListener("mousedown", onDoc);
      return () => document.removeEventListener("mousedown", onDoc);
    }
  }, [stateUtDropdownOpen]);

  useEffect(() => {
    const onDoc = (e) => {
      if (
        detailedStateUtDropdownRef.current &&
        !detailedStateUtDropdownRef.current.contains(e.target)
      ) {
        setDetailedStateUtDropdownOpen(false);
      }
    };
    if (detailedStateUtDropdownOpen) {
      document.addEventListener("mousedown", onDoc);
      return () => document.removeEventListener("mousedown", onDoc);
    }
  }, [detailedStateUtDropdownOpen]);

  const handleTaxInvoiceApply = async () => {
    setTaxInvoiceLoading(true);
    try {
      const response = await CommissionService.getTaxInvoiceSummary({
        year: taxInvoiceYear,
        month: taxInvoiceMonth,
      });
      const payload = response?.data;
      setTaxInvoiceData(payload || null);
      setTaxInvoiceApplied({ month: taxInvoiceMonth, year: taxInvoiceYear });
      toast.success("Tax invoice data loaded");
    } catch (error) {
      console.error("Error loading tax invoice:", error);
    } finally {
      setTaxInvoiceLoading(false);
    }
  };

  const handleTaxInvoiceClear = () => {
    setTaxInvoiceApplied(null);
    setTaxInvoiceData(null);
  };

  const handleStatewiseApply = async () => {
    if (!statewiseStartDate || !statewiseEndDate) {
      toast.warning("Please select start and end dates");
      return;
    }
    if (statewiseStartDate > statewiseEndDate) {
      toast.warning("Start date must be on or before end date");
      return;
    }
    setStatewiseLoading(true);
    try {
      const response = await CommissionService.getTaxInvoiceStatewise({
        startDate: statewiseStartDate,
        endDate: statewiseEndDate,
      });
      setStatewiseData(response?.data || null);
      setStatewiseApplied({
        startDate: statewiseStartDate,
        endDate: statewiseEndDate,
      });
      toast.success("Statewise report loaded");
    } catch (error) {
      console.error("Error loading statewise tax data:", error);
    } finally {
      setStatewiseLoading(false);
    }
  };

  const handleStatewiseClear = () => {
    setStatewiseApplied(null);
    setStatewiseData(null);
  };

  const formatInr2 = (n) =>
    Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatStateLabel = (state) =>
    String(state || "")
      .replace(/\d+/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const handleStatewiseExportCsv = () => {
    if (!statewiseData || !statewiseApplied) return;
    const dataRows = statewiseData.rows || [];
    if (dataRows.length === 0) {
      toast.warning("No data rows to export");
      return;
    }
    const esc = (val) => {
      const s = val === null || val === undefined ? "" : String(val);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const num = (n) => esc(Number(n || 0).toFixed(2));
    const headerCols = [
      "State",
      "Taxable Value",
      "IGST (18%)",
      "Total (Taxable + IGST)",
    ];
    const lines = [
      esc("Statewise settlement (PhonePe net amount)"),
      [esc("Period"), esc(`${statewiseApplied.startDate} to ${statewiseApplied.endDate}`), "", ""].join(","),
      [esc("Settlement rows"), String(statewiseData.settlementRowCount ?? 0), "", ""].join(","),
      "",
      headerCols.map((h) => esc(h)).join(","),
    ];
    for (const row of dataRows) {
      lines.push([
        esc(formatStateLabel(row.state)),
        num(row.taxableExGst),
        num(row.gstAmount),
        num(row.paidAmount),
      ].join(","));
    }
    if (statewiseData.totals) {
      lines.push(
        [esc("Total"), num(statewiseData.totals.taxableExGst), num(statewiseData.totals.gstAmount), num(statewiseData.totals.paidAmount)].join(",")
      );
    }
    const bom = "\uFEFF";
    const blob = new Blob([bom + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `statewise-settlement-${statewiseApplied.startDate}-to-${statewiseApplied.endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  const handleTaxInvoiceDownloadPdf = async () => {
    const el = taxInvoiceRef.current;
    if (!el) return;
    setTaxInvoicePdfLoading(true);
    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2;
      const ratio = Math.min(maxW / canvas.width, maxH / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      const x = (pageW - w) / 2;
      const y = margin;
      pdf.addImage(imgData, "PNG", x, y, w, h);
      const mon =
        taxInvoiceApplied && MONTH_ABBR[taxInvoiceApplied.month - 1]
          ? MONTH_ABBR[taxInvoiceApplied.month - 1]
          : "INV";
      const yr = taxInvoiceApplied?.year ?? "";
      pdf.save(`UDR-IGST-${mon}-${yr}.pdf`);
      toast.success("Invoice downloaded");
    } catch (err) {
      console.error("PDF export failed:", err);
      toast.error("Could not generate PDF");
    } finally {
      setTaxInvoicePdfLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleSummaryStartDateChange = (value) => {
    setFilters((f) => ({ ...f, startDate: value }));
    setSummaryDateFilterApplied(false);
    setSummaryAppliedComparison(null);
    setSummaryCompareData(null);
  };

  const handleSummaryEndDateChange = (value) => {
    setFilters((f) => ({ ...f, endDate: value }));
    setSummaryDateFilterApplied(false);
    setSummaryAppliedComparison(null);
    setSummaryCompareData(null);
  };

  const handleDetailedStartDateChange = (value) => {
    setDetailedFilters((f) => ({ ...f, startDate: value, page: 1 }));
    setDetailedDateFilterApplied(false);
    setDetailedAppliedComparison(null);
    setDetailedCompareData(null);
  };

  const handleDetailedEndDateChange = (value) => {
    setDetailedFilters((f) => ({ ...f, endDate: value, page: 1 }));
    setDetailedDateFilterApplied(false);
    setDetailedAppliedComparison(null);
    setDetailedCompareData(null);
  };

  const handleSummaryCompareToggle = (enabled) => {
    setSummaryCompareEnabled(enabled);
    if (!enabled) {
      setSummaryAppliedComparison(null);
      setSummaryCompareData(null);
      setSummaryCompareMode("previous_period");
    }
  };

  const handleDetailedCompareToggle = (enabled) => {
    setDetailedCompareEnabled(enabled);
    if (!enabled) {
      setDetailedAppliedComparison(null);
      setDetailedCompareData(null);
      setDetailedCompareMode("previous_period");
    }
  };

  const handleSummaryApplyComparison = () => {
    if (!summaryCompareEnabled) return;
    const range = computeComparisonRange(
      summaryCompareMode,
      summaryMainStartYmd,
      summaryMainEndYmd,
      summaryCompareStartDate,
      summaryCompareEndDate
    );
    if (!range) return;
    setSummaryAppliedComparison(range);
  };

  const handleDetailedApplyComparison = () => {
    if (!detailedCompareEnabled) return;
    const range = computeComparisonRange(
      detailedCompareMode,
      detailedMainStartYmd,
      detailedMainEndYmd,
      detailedCompareStartDate,
      detailedCompareEndDate
    );
    if (!range) return;
    setDetailedAppliedComparison(range);
  };

  const summaryCanApplyComparison =
    summaryDateFilterApplied &&
    summaryCompareEnabled &&
    summaryCompareMode !== "none" &&
    (summaryCompareMode === "custom"
      ? Boolean(summaryCompareStartDate && summaryCompareEndDate)
      : summaryHasMainDateRange);

  const detailedCanApplyComparison =
    detailedDateFilterApplied &&
    detailedCompareEnabled &&
    detailedCompareMode !== "none" &&
    (detailedCompareMode === "custom"
      ? Boolean(detailedCompareStartDate && detailedCompareEndDate)
      : detailedHasMainDateRange);

  const summaryShowComparison = Boolean(summaryAppliedComparison && summaryCompareData);
  const detailedShowComparison = Boolean(detailedAppliedComparison && detailedCompareData);

  const handleDetailedFilterChange = (key, value) => {
    setDetailedFilters({ ...detailedFilters, [key]: value, page: 1 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDetailedSortSet = (key, dir) => {
    setDetailedFilters((prev) => ({ ...prev, sortBy: key, sortOrder: dir, page: 1 }));
    fetchDetailedReport(1, { sortBy: key, sortOrder: dir });
  };

  const handleSummarySortSet = (key, dir) => {
    setSummarySort({ key, dir });
  };

  const handleSummarySortLabelClick = (key) => {
    setSummarySort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  };

  const handleDetailedSortLabelClick = (key) => {
    setDetailedFilters((prev) => {
      const dir =
        prev.sortBy === key && prev.sortOrder === "asc" ? "desc" : "asc";
      fetchDetailedReport(1, { sortBy: key, sortOrder: dir });
      return { ...prev, sortBy: key, sortOrder: dir, page: 1 };
    });
  };

  // Sort applies to the current API page only; global sort would need backend sortBy/order.
  const summaryRows = useMemo(() => {
    const report = reportData?.report;
    if (!report?.length) return [];
    const makeRow = (dayReport, statusKey, statusLabel, statusSort) => ({
      rowId: `${dayReport.date}-${statusKey}`,
      date: dayReport.date,
      dateTs: dayReport.date ? new Date(dayReport.date).getTime() : 0,
      status: statusLabel,
      statusSort,
      totalAmount: dayReport[statusKey]?.totalAmount || 0,
      totalCommission: dayReport[statusKey]?.totalCommission || 0,
      totalGst: dayReport[statusKey]?.totalGst || 0,
      totalRideAmount: dayReport[statusKey]?.totalRideAmount || 0,
      count: dayReport[statusKey]?.count || 0,
    });
    return report.flatMap((dayReport) => {
      const rows = [
        makeRow(dayReport, "done", "Done", 1),
        makeRow(dayReport, "pending", "Pending", 2),
      ];
      if (dayReport.failed?.count > 0) {
        rows.push(makeRow(dayReport, "failed", "Failed", 3));
      }
      rows.push(makeRow(dayReport, "total", "Total", 4));
      return rows;
    });
  }, [reportData?.report]);

  const sortedSummaryRows = useMemo(() => {
    if (!summaryRows.length) return [];
    if (!summarySort.key) return summaryRows;
    const copy = [...summaryRows];
    copy.sort((a, b) => compareSummaryRows(a, b, summarySort.key, summarySort.dir));
    return copy;
  }, [summaryRows, summarySort.key, summarySort.dir]);

  const beforeVisibleCount = useMemo(
    () => DETAILED_COL_BEFORE.filter((k) => detailedColumnVisibility[k]).length,
    [detailedColumnVisibility]
  );
  const afterVisibleCount = useMemo(
    () => DETAILED_COL_AFTER.filter((k) => detailedColumnVisibility[k]).length,
    [detailedColumnVisibility]
  );
  const detailedTableColCount = 1 + beforeVisibleCount + afterVisibleCount + 1 + 1;
  const hasDetailedDataColumns = beforeVisibleCount + afterVisibleCount > 0;

  useEffect(() => {
    if (!detailedColumnsMenuOpen) return;
    const onDoc = (e) => {
      if (
        detailedColumnsMenuRef.current &&
        !detailedColumnsMenuRef.current.contains(e.target)
      ) {
        setDetailedColumnsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [detailedColumnsMenuOpen]);

  const sortChevronBtnClass = (active) =>
    `p-0.5 rounded shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 ${
      active
        ? "text-indigo-600 bg-indigo-50"
        : "text-gray-400 hover:text-gray-600"
    }`;

  const renderDetailedSortArrows = (sortKey) => {
    const active = detailedFilters.sortBy === sortKey;
    const ascOn = active && detailedFilters.sortOrder === "asc";
    const descOn = active && detailedFilters.sortOrder === "desc";
    return (
      <span className="inline-flex flex-row items-center shrink-0 gap-0.5">
        <button
          type="button"
          aria-label="Sort ascending"
          aria-pressed={ascOn}
          className={sortChevronBtnClass(ascOn)}
          onClick={(e) => {
            e.stopPropagation();
            handleDetailedSortSet(sortKey, "asc");
          }}
        >
          <FiChevronUp className="w-4 h-4" aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Sort descending"
          aria-pressed={descOn}
          className={sortChevronBtnClass(descOn)}
          onClick={(e) => {
            e.stopPropagation();
            handleDetailedSortSet(sortKey, "desc");
          }}
        >
          <FiChevronDown className="w-4 h-4" aria-hidden />
        </button>
      </span>
    );
  };

  const renderDetailedSortableTh = (sortKey, label) => (
    <th
      key={sortKey}
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
    >
      <div className="inline-flex items-center gap-1.5 max-w-full">
        <button
          type="button"
          onClick={() => handleDetailedSortLabelClick(sortKey)}
          className="text-left min-w-0 hover:text-gray-800"
        >
          <span className="break-words">{label}</span>
        </button>
        {renderDetailedSortArrows(sortKey)}
      </div>
    </th>
  );

  const renderSummarySortArrows = (sortKey) => {
    const active = summarySort.key === sortKey;
    const ascOn = active && summarySort.dir === "asc";
    const descOn = active && summarySort.dir === "desc";
    return (
      <span className="inline-flex flex-row items-center shrink-0 gap-0.5">
        <button
          type="button"
          aria-label="Sort ascending"
          aria-pressed={ascOn}
          className={sortChevronBtnClass(ascOn)}
          onClick={(e) => {
            e.stopPropagation();
            handleSummarySortSet(sortKey, "asc");
          }}
        >
          <FiChevronUp className="w-4 h-4" aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Sort descending"
          aria-pressed={descOn}
          className={sortChevronBtnClass(descOn)}
          onClick={(e) => {
            e.stopPropagation();
            handleSummarySortSet(sortKey, "desc");
          }}
        >
          <FiChevronDown className="w-4 h-4" aria-hidden />
        </button>
      </span>
    );
  };

  const renderSummarySortableTh = (sortKey, label) => (
    <th
      key={sortKey}
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
    >
      <div className="inline-flex items-center gap-1.5 max-w-full">
        <button
          type="button"
          onClick={() => handleSummarySortLabelClick(sortKey)}
          className="text-left min-w-0 hover:text-gray-800"
        >
          <span className="break-words">{label}</span>
        </button>
        {renderSummarySortArrows(sortKey)}
      </div>
    </th>
  );

  const renderDetailedSortableCornerTh = (sortKey, label, rowSpan) => (
    <th
      rowSpan={rowSpan}
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-bottom bg-gray-50"
    >
      <div className="inline-flex items-center gap-1.5 max-w-full">
        <button
          type="button"
          onClick={() => handleDetailedSortLabelClick(sortKey)}
          className="text-left min-w-0 hover:text-gray-800"
        >
          <span className="break-words">{label}</span>
        </button>
        {renderDetailedSortArrows(sortKey)}
      </div>
    </th>
  );

  const getPaymentStatusBadge = (status) => {
    const statusColors = {
      PAID: "bg-green-100 text-green-800",
      PENDING: "bg-yellow-100 text-yellow-800",
      FAILED: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status] || "bg-gray-100 text-gray-800"
          }`}
      >
        {status}
      </span>
    );
  };

  const handlePaymentStatusUpdate = async (driverPhone, paymentStatus, commissionAmount) => {
    if (!driverPhone || !paymentStatus) return;

    setUpdatingCommissionId(driverPhone);
    try {
      await CommissionService.updateCommissionPaymentStatus(driverPhone, commissionAmount);
      toast.success("Payment status updated successfully");
      fetchDetailedReport(detailedPagination.page || 1);
    } catch (error) {
      console.error("Error updating payment status:", error);
    } finally {
      setUpdatingCommissionId(null);
    }
  };

  const fetchSettlementReport = async (page = 1) => {
    setSettlementLoading(true);
    try {
      const params = {
        page,
        limit: settlementPagination.limit || 10,
      };
      if (settlementFilters.startDate) params.startDate = settlementFilters.startDate;
      if (settlementFilters.endDate) params.endDate = settlementFilters.endDate;
      if (settlementFilters.driverId) params.driverId = settlementFilters.driverId;
      if (settlementFilters.paymentOrderId)
        params.paymentOrderId = settlementFilters.paymentOrderId;
      if (settlementFilters.paymentTransactionId)
        params.paymentTransactionId = settlementFilters.paymentTransactionId;

      const response = await CommissionService.getPhonePeSettlementReport(params);
      const data = response?.data || {};
      setSettlementRows(data.rows || []);
      setSettlementPagination(
        data.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          pages: 0,
        }
      );
    } catch (error) {
      console.error("Error fetching settlement report:", error);
    } finally {
      setSettlementLoading(false);
    }
  };

  const handleSettlementFilterChange = (key, value) => {
    setSettlementFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleUploadSettlement = async () => {
    if (!settlementFile) {
      toast.warning("Please choose .csv or .xlsx file");
      return;
    }
    setSettlementLoading(true);
    try {
      const response = await CommissionService.uploadPhonePeSettlementReport(settlementFile);
      const summary = response?.data || null;
      setSettlementSummary(summary);
      toast.success("Settlement report uploaded successfully");
      setSettlementFile(null);
      await fetchSettlementReport(1);
    } catch (error) {
      console.error("Error uploading settlement file:", error);
    } finally {
      setSettlementLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* View Toggle */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveView("summary")}
            className={`px-4 py-2 rounded-md font-medium ${activeView === "summary"
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
          >
            <FiFileText className="inline mr-2" />
            Summary Report
          </button>
          <button
            onClick={() => setActiveView("detailed")}
            className={`px-4 py-2 rounded-md font-medium ${activeView === "detailed"
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
          >
            <FiTrendingUp className="inline mr-2" />
            Detailed Report
          </button>
          <button
            onClick={() => setActiveView("taxInvoice")}
            className={`px-4 py-2 rounded-md font-medium ${activeView === "taxInvoice"
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
          >
            &#8377;
            Tax Invoice
          </button>
          <button
            onClick={() => setActiveView("statewise")}
            className={`px-4 py-2 rounded-md font-medium ${activeView === "statewise"
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
          >
            <FiMapPin className="inline mr-2" />
            Display statewise
          </button>
        </div>
      </div>

      {/* Summary View */}
      {activeView === "summary" && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <ReportDateRangeFilters
                  variant="inline"
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  onStartDateChange={handleSummaryStartDateChange}
                  onEndDateChange={handleSummaryEndDateChange}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Driver name or phone (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Driver name or phone..."
                    value={filters.driverSearch}
                    onChange={(e) => handleFilterChange("driverSearch", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="min-w-0 relative" ref={stateUtDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    States & UT
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setStateUtDropdownOpen((o) => !o);
                      if (!stateUtDropdownOpen) setStateUtSearch("");
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm text-left flex items-center justify-between hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <span className={filters.stateUt ? "text-gray-900" : "text-gray-500"}>
                      {getStateUtDisplayLabel(filters.stateUt)}
                    </span>
                    <FaSearch className="text-gray-400 ml-2 shrink-0" />
                  </button>
                  {stateUtDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden">
                      <div className="p-2 border-b border-gray-100">
                        <input
                          type="text"
                          value={stateUtSearch}
                          onChange={(e) => setStateUtSearch(e.target.value)}
                          placeholder="Search state or UT..."
                          className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                      </div>
                      <ul className="max-h-48 overflow-y-auto py-1">
                        {STATE_UT_OPTIONS.filter((opt) => {
                          if (opt.type === "header") {
                            return !stateUtSearch.trim();
                          }
                          return (
                            !stateUtSearch.trim() ||
                            opt.label.toLowerCase().includes(stateUtSearch.trim().toLowerCase())
                          );
                        }).map((opt) =>
                          opt.type === "header" ? (
                            <li key="state-ut-header">
                              <div className="px-4 py-2 text-xs font-semibold bg-yellow-50 text-gray-700 border-y border-yellow-100">
                                {opt.label}
                              </div>
                            </li>
                          ) : (
                            <li key={opt.value || "__state_empty__"}>
                              <button
                                type="button"
                                onClick={() => {
                                  setFilters((f) => ({ ...f, stateUt: opt.value }));
                                  setStateUtDropdownOpen(false);
                                  setStateUtSearch("");
                                }}
                                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 ${
                                  filters.stateUt === opt.value
                                    ? "bg-blue-50 text-blue-700 font-medium"
                                    : "text-gray-800"
                                }`}
                              >
                                {opt.label}
                              </button>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => fetchReport({ markDateFilterApplied: true })}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    <FiCalendar className="inline mr-2" />
                    Generate Report
                  </button>
                </div>
              </div>

              <ReportCompareFilters
                compareEnabled={summaryCompareEnabled}
                onCompareToggle={handleSummaryCompareToggle}
                compareMode={summaryCompareMode}
                onCompareModeChange={setSummaryCompareMode}
                compareStartDate={summaryCompareStartDate}
                compareEndDate={summaryCompareEndDate}
                onCompareStartChange={setSummaryCompareStartDate}
                onCompareEndChange={setSummaryCompareEndDate}
                onApplyComparison={handleSummaryApplyComparison}
                onClearComparison={() => {
                  setSummaryAppliedComparison(null);
                  setSummaryCompareData(null);
                  setSummaryCompareMode("previous_period");
                }}
                canApplyComparison={summaryCanApplyComparison}
                comparisonLoading={summaryCompareLoading}
                appliedComparison={summaryAppliedComparison}
                hasMainDateRange={summaryHasMainDateRange}
                showApplyComparisonButton={summaryDateFilterApplied}
                applyFilterHint="Choose Start Date and End Date, then click Generate Report to enable comparison."
              />
            </div>
          </div>

          {loading && <Loader />}

          {reportData && (
            <>
              {summaryAppliedComparison && (
                <p className="text-sm text-gray-600">
                  Comparing{" "}
                  {formatBreakdownDateRangeLabel(summaryMainStartYmd, summaryMainEndYmd)} vs{" "}
                  {summaryAppliedComparison.label} (
                  {formatBreakdownDateRangeLabel(
                    summaryAppliedComparison.startDate,
                    summaryAppliedComparison.endDate
                  )}
                  )
                </p>
              )}

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-500">
                        Total Commission Due
                      </div>
                      <div className="mt-2 text-2xl font-bold text-gray-900">
                        {formatCurrency(reportData.summary?.totals?.total?.totalAmount || 0)}
                      </div>
                      {summaryShowComparison &&
                        renderComparisonDelta(
                          reportData.summary?.totals?.total?.totalAmount || 0,
                          summaryCompareData?.summary?.totals?.total?.totalAmount ?? null,
                          formatCurrency
                        )}
                    </div>
                    <span className="text-blue-600 text-3xl">&#8377;</span>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-500">
                        Total Commission
                      </div>
                      <div className="mt-2 text-2xl font-bold text-green-600">
                        {formatCurrency(reportData.summary?.totals?.total?.totalCommission || 0)}
                      </div>
                      {summaryShowComparison &&
                        renderComparisonDelta(
                          reportData.summary?.totals?.total?.totalCommission || 0,
                          summaryCompareData?.summary?.totals?.total?.totalCommission ?? null,
                          formatCurrency
                        )}
                    </div>
                    <FiTrendingUp className="text-green-600 text-3xl" />
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-500">
                        Total GST
                      </div>
                      <div className="mt-2 text-2xl font-bold text-purple-600">
                        {formatCurrency(reportData.summary?.totals?.total?.totalGst || 0)}
                      </div>
                      {summaryShowComparison &&
                        renderComparisonDelta(
                          reportData.summary?.totals?.total?.totalGst || 0,
                          summaryCompareData?.summary?.totals?.total?.totalGst ?? null,
                          formatCurrency
                        )}
                    </div>
                    <FiFileText className="text-purple-600 text-3xl" />
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-500">
                        Total Rides
                      </div>
                      <div className="mt-2 text-2xl font-bold text-orange-600">
                        {reportData.summary?.totals?.total?.count || 0}
                      </div>
                      {summaryShowComparison &&
                        renderComparisonDelta(
                          reportData.summary?.totals?.total?.count || 0,
                          summaryCompareData?.summary?.totals?.total?.count ?? null,
                          (v) => Number(v || 0).toLocaleString()
                        )}
                    </div>
                    <FiUser className="text-orange-600 text-3xl" />
                  </div>
                </div>
              </div>

              {/* Status Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Paid (Done)</h3>
                  <div className="space-y-2">
                    {renderStatusBreakdownMetrics(
                      reportData.summary?.totals?.done,
                      summaryCompareData?.summary?.totals?.done,
                      summaryShowComparison
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rides:</span>
                      <span className="font-medium">{reportData.summary?.totals?.done?.count || 0}</span>
                    </div>
                    {summaryShowComparison &&
                      renderComparisonDelta(
                        reportData.summary?.totals?.done?.count || 0,
                        summaryCompareData?.summary?.totals?.done?.count ?? null,
                        (v) => `${Number(v || 0).toLocaleString()} rides`
                      )}
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Pending</h3>
                  <div className="space-y-2">
                    {renderStatusBreakdownMetrics(
                      reportData.summary?.totals?.pending,
                      summaryCompareData?.summary?.totals?.pending,
                      summaryShowComparison
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rides:</span>
                      <span className="font-medium">{reportData.summary?.totals?.pending?.count || 0}</span>
                    </div>
                    {summaryShowComparison &&
                      renderComparisonDelta(
                        reportData.summary?.totals?.pending?.count || 0,
                        summaryCompareData?.summary?.totals?.pending?.count ?? null,
                        (v) => `${Number(v || 0).toLocaleString()} rides`
                      )}
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Failed</h3>
                  <div className="space-y-2">
                    {renderStatusBreakdownMetrics(
                      reportData.summary?.totals?.failed,
                      summaryCompareData?.summary?.totals?.failed,
                      summaryShowComparison
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rides:</span>
                      <span className="font-medium">{reportData.summary?.totals?.failed?.count || 0}</span>
                    </div>
                    {summaryShowComparison &&
                      renderComparisonDelta(
                        reportData.summary?.totals?.failed?.count || 0,
                        summaryCompareData?.summary?.totals?.failed?.count ?? null,
                        (v) => `${Number(v || 0).toLocaleString()} rides`
                      )}
                  </div>
                </div>
              </div>

              {/* Daily Report Table */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Daily Breakdown</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {renderSummarySortableTh("date", "Date")}
                        {renderSummarySortableTh("status", "Status")}
                        {renderSummarySortableTh("totalAmount", "Total Amount")}
                        {renderSummarySortableTh("totalCommission", "Commission")}
                        {renderSummarySortableTh("totalGst", "GST")}
                        {renderSummarySortableTh("totalRideAmount", "Ride Amount")}
                        {renderSummarySortableTh("count", "Count")}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedSummaryRows.map((row) => (
                        <tr
                          key={row.rowId}
                          className={`hover:bg-gray-50 ${
                            row.status === "Total" ? "bg-gray-50 font-semibold" : ""
                          }`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatDate(row.date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {row.status === "Done" && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                Done
                              </span>
                            )}
                            {row.status === "Pending" && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                                Pending
                              </span>
                            )}
                            {row.status === "Failed" && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                Failed
                              </span>
                            )}
                            {row.status === "Total" && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                Total
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(row.totalAmount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(row.totalCommission)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(row.totalGst)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(row.totalRideAmount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Detailed View */}
      {activeView === "detailed" && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Upload PhonePay SettleMent Report
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <input
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={(e) => setSettlementFile(e.target.files?.[0] || null)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Required headers: Field-1, PhonePe Transaction ID, Merchant Order Id
                </p>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleUploadSettlement}
                  disabled={settlementLoading}
                  className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-60"
                >
                  <FiUpload className="inline mr-2" />
                  {settlementLoading ? "Uploading..." : "Upload Report"}
                </button>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => fetchSettlementReport(1)}
                  disabled={settlementLoading}
                  className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 disabled:opacity-60"
                >
                  Refresh Settlement Data
                </button>
              </div>
            </div>

            {settlementSummary && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
                <div className="bg-blue-50 rounded p-2">Rows: {settlementSummary.totalRows || 0}</div>
                <div className="bg-green-50 rounded p-2">Matched: {settlementSummary.matchedRows || 0}</div>
                <div className="bg-emerald-50 rounded p-2">Inserted: {settlementSummary.insertedRows || 0}</div>
                <div className="bg-yellow-50 rounded p-2">Duplicates: {settlementSummary.duplicateRows || 0}</div>
                <div className="bg-orange-50 rounded p-2">
                  Unmatched: {settlementSummary.skippedUnmatchedRows || 0}
                </div>
                <div className="bg-red-50 rounded p-2">Invalid: {settlementSummary.skippedInvalidRows || 0}</div>
              </div>
            )}

            <div className="my-6 border-t border-gray-200" />
            <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <ReportDateRangeFilters
                  variant="inline"
                  startDate={detailedFilters.startDate}
                  endDate={detailedFilters.endDate}
                  onStartDateChange={handleDetailedStartDateChange}
                  onEndDateChange={handleDetailedEndDateChange}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Driver name or phone (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Driver name or phone..."
                    value={detailedFilters.driverSearch}
                    onChange={(e) => handleDetailedFilterChange("driverSearch", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="min-w-0 relative" ref={detailedStateUtDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State & UT
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setDetailedStateUtDropdownOpen((o) => !o);
                      if (!detailedStateUtDropdownOpen) setDetailedStateUtSearch("");
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm text-left flex items-center justify-between hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <span className={detailedFilters.stateUt ? "text-gray-900" : "text-gray-500"}>
                      {getStateUtDisplayLabel(detailedFilters.stateUt)}
                    </span>
                    <FaSearch className="text-gray-400 ml-2 shrink-0" />
                  </button>
                  {detailedStateUtDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden">
                      <div className="p-2 border-b border-gray-100">
                        <input
                          type="text"
                          value={detailedStateUtSearch}
                          onChange={(e) => setDetailedStateUtSearch(e.target.value)}
                          placeholder="Search state or UT..."
                          className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                      </div>
                      <ul className="max-h-48 overflow-y-auto py-1">
                        {STATE_UT_OPTIONS.filter((opt) => {
                          if (opt.type === "header") {
                            return !detailedStateUtSearch.trim();
                          }
                          return (
                            !detailedStateUtSearch.trim() ||
                            opt.label.toLowerCase().includes(detailedStateUtSearch.trim().toLowerCase())
                          );
                        }).map((opt) =>
                          opt.type === "header" ? (
                            <li key="detailed-state-ut-header">
                              <div className="px-4 py-2 text-xs font-semibold bg-yellow-50 text-gray-700 border-y border-yellow-100">
                                {opt.label}
                              </div>
                            </li>
                          ) : (
                            <li key={opt.value || "__detailed_state_empty__"}>
                              <button
                                type="button"
                                onClick={() => {
                                  setDetailedFilters((f) => ({ ...f, stateUt: opt.value, page: 1 }));
                                  setDetailedStateUtDropdownOpen(false);
                                  setDetailedStateUtSearch("");
                                }}
                                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 ${
                                  detailedFilters.stateUt === opt.value
                                    ? "bg-blue-50 text-blue-700 font-medium"
                                    : "text-gray-800"
                                }`}
                              >
                                {opt.label}
                              </button>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Status
                  </label>
                  <select
                    value={detailedFilters.paymentStatus}
                    onChange={(e) => handleDetailedFilterChange("paymentStatus", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">All Statuses</option>
                    <option value="PAID">Paid</option>
                    <option value="PENDING">Pending</option>
                    <option value="FAILED">Failed</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() =>
                      fetchDetailedReport(1, null, { markDateFilterApplied: true })
                    }
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    <FiCalendar className="inline mr-2" />
                    Search
                  </button>
                </div>
              </div>

              <ReportCompareFilters
                compareEnabled={detailedCompareEnabled}
                onCompareToggle={handleDetailedCompareToggle}
                compareMode={detailedCompareMode}
                onCompareModeChange={setDetailedCompareMode}
                compareStartDate={detailedCompareStartDate}
                compareEndDate={detailedCompareEndDate}
                onCompareStartChange={setDetailedCompareStartDate}
                onCompareEndChange={setDetailedCompareEndDate}
                onApplyComparison={handleDetailedApplyComparison}
                onClearComparison={() => {
                  setDetailedAppliedComparison(null);
                  setDetailedCompareData(null);
                  setDetailedCompareMode("previous_period");
                }}
                canApplyComparison={detailedCanApplyComparison}
                comparisonLoading={detailedCompareLoading}
                appliedComparison={detailedAppliedComparison}
                hasMainDateRange={detailedHasMainDateRange}
                showApplyComparisonButton={detailedDateFilterApplied}
                applyFilterHint="Choose Start Date and End Date, then click Search to enable comparison."
              />
            </div>
          </div>

          {loading && <Loader />}
          {settlementLoading && <Loader />}

          {detailedData && (
            <>
              {detailedAppliedComparison && (
                <p className="text-sm text-gray-600">
                  Comparing{" "}
                  {formatBreakdownDateRangeLabel(detailedMainStartYmd, detailedMainEndYmd)} vs{" "}
                  {detailedAppliedComparison.label} (
                  {formatBreakdownDateRangeLabel(
                    detailedAppliedComparison.startDate,
                    detailedAppliedComparison.endDate
                  )}
                  )
                </p>
              )}

              {/* Totals Summary */}
              {detailedData.totals && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {sortDetailedTotalsEntries(detailedData.totals).map(([status, totals]) => {
                    const compareTotals = detailedCompareData?.totals?.[status];
                    return (
                    <div key={status} className="bg-white p-6 rounded-lg shadow">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">{status}</h3>
                      <div className="space-y-2">
                        {renderStatusBreakdownMetrics(
                          totals,
                          compareTotals,
                          detailedShowComparison
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Rides:</span>
                          <span className="font-medium">{totals.count || 0}</span>
                        </div>
                        {detailedShowComparison &&
                          renderComparisonDelta(
                            totals.count || 0,
                            compareTotals?.count ?? null,
                            (v) => `${Number(v || 0).toLocaleString()} rides`
                          )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}

              {/* Detailed Table — client-side sort on current page only; column visibility for data columns */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h3 className="text-lg font-medium text-gray-900">Commission Details</h3>
                  <div className="relative shrink-0" ref={detailedColumnsMenuRef}>
                    <button
                      type="button"
                      onClick={() => setDetailedColumnsMenuOpen((o) => !o)}
                      className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-200 text-sm font-medium"
                    >
                      <FiColumns className="w-4 h-4" aria-hidden />
                      Columns
                    </button>
                    {detailedColumnsMenuOpen && (
                      <div className="absolute right-0 mt-1 z-20 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
                        <p className="text-xs font-semibold text-gray-500 mb-2">Show columns</p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {[...DETAILED_COL_BEFORE, ...DETAILED_COL_AFTER].map((colId) => (
                            <label key={colId} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!detailedColumnVisibility[colId]}
                                onChange={() =>
                                  setDetailedColumnVisibility((prev) => ({
                                    ...prev,
                                    [colId]: !prev[colId],
                                  }))
                                }
                              />
                              <span>{DETAILED_COLUMN_LABELS[colId]}</span>
                            </label>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setDetailedColumnVisibility({ ...DEFAULT_DETAILED_COLUMN_VISIBILITY })
                          }
                          className="mt-3 w-full text-xs text-blue-600 hover:underline"
                        >
                          Reset to default
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
                      <tr>
                        {renderDetailedSortableCornerTh(
                          "driverName",
                          "Driver",
                          hasDetailedDataColumns ? 2 : 1
                        )}
                        {beforeVisibleCount > 0 ? (
                          <th
                            colSpan={beforeVisibleCount}
                            className="px-6 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200 bg-gray-50"
                          >
                            Before Phonepay Data
                          </th>
                        ) : null}
                        {afterVisibleCount > 0 ? (
                          <th
                            colSpan={afterVisibleCount}
                            className="px-6 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200 bg-gray-50"
                          >
                            After Phonepay Data
                          </th>
                        ) : null}
                        {renderDetailedSortableCornerTh(
                          "status",
                          "Status",
                          hasDetailedDataColumns ? 2 : 1
                        )}
                        {renderDetailedSortableCornerTh(
                          "date",
                          "Date",
                          hasDetailedDataColumns ? 2 : 1
                        )}
                      </tr>
                      {hasDetailedDataColumns ? (
                        <tr>
                          {DETAILED_COL_BEFORE.filter((id) => detailedColumnVisibility[id]).map(
                            (id) => renderDetailedSortableTh(id, DETAILED_COLUMN_LABELS[id])
                          )}
                          {DETAILED_COL_AFTER.filter((id) => detailedColumnVisibility[id]).map(
                            (id) => renderDetailedSortableTh(id, DETAILED_COLUMN_LABELS[id])
                          )}
                        </tr>
                      ) : null}
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {detailedData?.commissions?.length > 0 ? (
                        detailedData.commissions.map((commission) => (
                          <tr key={commission._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {commission.driverId?.fullName || "N/A"}
                              </div>
                              <div className="text-sm text-gray-500">
                                {commission.driverId?.phone || "N/A"}
                              </div>
                            </td>
                            {detailedColumnVisibility.rideDetails ? (
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">
                                  <div className="font-medium">
                                    {commission.rideId?.pickup?.address || "N/A"}
                                  </div>
                                  <div className="text-gray-500">
                                    → {commission.rideId?.dropoff?.address || "N/A"}
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1">
                                    {commission.rideId?.completedAt
                                      ? formatDate(commission.rideId.completedAt)
                                      : "N/A"}
                                  </div>
                                </div>
                              </td>
                            ) : null}
                            {detailedColumnVisibility.rideFare ? (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(commission.rideId?.fare || 0)}
                              </td>
                            ) : null}
                            {detailedColumnVisibility.driverEarnings ? (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(commission.rideAmount || 0)}
                              </td>
                            ) : null}
                            {detailedColumnVisibility.commissionPct ? (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {commission.commissionPercentage || 0}%
                              </td>
                            ) : null}
                            {detailedColumnVisibility.commission ? (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(commission.commissionAmount || 0)}
                              </td>
                            ) : null}
                            {detailedColumnVisibility.gstBefore ? (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(commission.gstAmount || 0)}
                              </td>
                            ) : null}
                            {detailedColumnVisibility.totalDue ? (
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {formatCurrency(commission.totalCommissionDue || 0)}
                              </td>
                            ) : null}
                            {detailedColumnVisibility.ids ? (
                              <td className="px-6 py-4 align-top min-w-[200px]">
                                <div className="text-sm space-y-2 max-w-xs">
                                  <div>
                                    <div className="text-xs text-gray-500">
                                      Driver ID (Field-1)
                                    </div>
                                    <div className="text-gray-900 font-mono text-sm break-all">
                                      {formatIdCellPart(getCommissionRefId(commission.driverId))}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">
                                      Ride ID (Our Reference)
                                    </div>
                                    <div className="text-gray-900 font-mono text-sm break-all">
                                      {formatIdCellPart(getCommissionRefId(commission.rideId))}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Merchant Order ID</div>
                                    <div className="text-gray-900 font-mono text-sm break-all">
                                      {formatMerchantOrTxnIdEmpty(commission.paymentOrderId)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">
                                      PhonePe Transaction ID
                                    </div>
                                    <div className="text-gray-900 font-mono text-sm break-all">
                                      {formatMerchantOrTxnIdEmpty(
                                        commission.paymentTransactionId
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            ) : null}
                            {detailedColumnVisibility.settlementAmount ? (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {commission.phonePeSettlementAmount != null
                                  ? formatCurrency(commission.phonePeSettlementAmount)
                                  : "—"}
                              </td>
                            ) : null}
                            {detailedColumnVisibility.commissionAfter ? (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {commission.phonePeAfterCommission != null
                                  ? formatCurrency(commission.phonePeAfterCommission)
                                  : "—"}
                              </td>
                            ) : null}
                            {detailedColumnVisibility.gstAfter ? (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {commission.phonePeAfterGst != null
                                  ? formatCurrency(commission.phonePeAfterGst)
                                  : "—"}
                              </td>
                            ) : null}
                            <td className="px-6 py-4 align-middle">
                              <div className="flex flex-col gap-2 min-w-[120px]">
                                {getPaymentStatusBadge(commission.paymentStatus)}
                                {detailedReportsPaymentStatus && (
                                  <>
                                    {commission.paymentStatus === "PENDING" && (
                                      <select
                                        value={commission.paymentStatus || "PENDING"}
                                        onChange={(e) =>
                                          handlePaymentStatusUpdate(
                                            commission.driverId?.phone,
                                            e.target.value,
                                            0
                                          )
                                        }
                                        disabled={
                                          updatingCommissionId === commission.driverId?.phone
                                        }
                                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs disabled:opacity-60"
                                      >
                                        <option value="PENDING">Pending</option>
                                        <option value="PAID">Paid</option>
                                        <option value="FAILED">Failed</option>
                                      </select>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(commission.createdAt)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={Math.max(detailedTableColCount, 1)}
                            className="px-6 py-4 text-center text-sm text-gray-500"
                          >
                            No commission records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {detailedPagination.pages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <button
                        onClick={() => fetchDetailedReport(detailedPagination.page - 1)}
                        disabled={detailedPagination.page === 1}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-700">
                        Page {detailedPagination.page} of {detailedPagination.pages} ({detailedPagination.total} total)
                      </span>
                      <button
                        onClick={() => fetchDetailedReport(detailedPagination.page + 1)}
                        disabled={detailedPagination.page === detailedPagination.pages}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {activeView === "taxInvoice" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Month &amp; Year
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <select
                      value={taxInvoiceMonth}
                      onChange={(e) => setTaxInvoiceMonth(Number(e.target.value))}
                      className="border border-gray-300 rounded-md px-3 py-2 min-w-[140px]"
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                        <option key={m} value={m}>
                          {format(new Date(2000, m - 1, 1), "MMMM")}
                        </option>
                      ))}
                    </select>
                    <select
                      value={taxInvoiceYear}
                      onChange={(e) => setTaxInvoiceYear(Number(e.target.value))}
                      className="border border-gray-300 rounded-md px-3 py-2 min-w-[100px]"
                    >
                      {taxInvoiceYearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleTaxInvoiceApply}
                    disabled={taxInvoiceLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-60"
                  >
                    {taxInvoiceLoading ? "Loading…" : "Apply"}
                  </button>
                  <button
                    type="button"
                    onClick={handleTaxInvoiceClear}
                    className="bg-gray-100 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-200"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>

          {taxInvoiceLoading && <Loader />}

          {taxInvoiceApplied && taxInvoiceData && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-lg font-medium text-gray-900">Tax Invoice (IGST) preview</h3>
                <button
                  type="button"
                  onClick={handleTaxInvoiceDownloadPdf}
                  disabled={taxInvoicePdfLoading}
                  className="inline-flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-60 text-sm font-medium"
                >
                  <FiDownload className="inline" />
                  {taxInvoicePdfLoading ? "Preparing…" : "Download Invoice"}
                </button>
              </div>
              <div className="p-6 overflow-x-auto">
                <div
                  ref={taxInvoiceRef}
                  className="max-w-3xl mx-auto bg-white text-gray-900 p-8 border border-gray-900 text-sm leading-relaxed"
                  style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
                >
                  <h1 className="text-center text-xl font-bold tracking-wide mb-8">
                    TAX INVOICE (IGST)
                  </h1>
                  <div className="space-y-1 mb-8 text-left">
                    <p className="font-bold text-base">
                      UDRIVO TECHNOLOGIES PRIVATE LIMITED
                    </p>
                    <p>GSTIN: 36AADCU6715C1ZI</p>
                    <p>1-98/3/5/23 TO 27, Jubilee Enclave, Madhapur,</p>
                    <p>Shaikpet, Hyderabad – 500081, Telangana</p>
                    <p className="pt-3">
                      <span className="font-semibold">Invoice No: </span>
                      {`UDR/IGST/${MONTH_ABBR[taxInvoiceApplied.month - 1]}/${taxInvoiceApplied.year}/001`}
                    </p>
                    <p>
                      <span className="font-semibold">Invoice Date: </span>
                      {format(
                        endOfMonth(new Date(taxInvoiceApplied.year, taxInvoiceApplied.month - 1, 1)),
                        "dd-MM-yyyy"
                      )}
                    </p>
                    <p>
                      <span className="font-semibold">Place of Supply: </span>
                      Inter-State (IGST)
                    </p>
                  </div>

                  <table className="w-full border-collapse border border-black text-xs sm:text-sm mb-8">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-black px-2 py-2 text-center font-bold">Sl. No</th>
                        <th className="border border-black px-2 py-2 text-center font-bold">
                          Description of Service
                        </th>
                        <th className="border border-black px-2 py-2 text-center font-bold">SAC</th>
                        <th className="border border-black px-2 py-2 text-center font-bold">
                          Taxable Value (Rs.)
                        </th>
                        <th className="border border-black px-2 py-2 text-center font-bold">
                          IGST @18% (Rs.)
                        </th>
                        <th className="border border-black px-2 py-2 text-center font-bold">
                          Total (Rs.)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-black px-2 py-3 text-center align-top">1</td>
                        <td className="border border-black px-2 py-3 text-left align-top">
                          Driver service facilitation / commission received from car drivers via
                          PhonePe
                        </td>
                        <td className="border border-black px-2 py-3 text-center align-top">998599</td>
                        <td className="border border-black px-2 py-3 text-right align-top whitespace-nowrap">
                          {Number(taxInvoiceData.taxableValue || 0).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="border border-black px-2 py-3 text-right align-top whitespace-nowrap">
                          {Number(taxInvoiceData.igstAmount || 0).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="border border-black px-2 py-3 text-right align-top whitespace-nowrap font-medium">
                          ₹{" "}
                          {Number(taxInvoiceData.totalAmount || 0).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="space-y-3 text-left">
                    <p>
                      <span className="font-bold">Total Invoice Value: </span>
                      Rs.{" "}
                      {Number(taxInvoiceData.totalAmount || 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                    <p>
                      <span className="font-bold">Amount in Words: </span>
                      {amountToWordsInr(Number(taxInvoiceData.totalAmount || 0))}
                    </p>
                    <div className="pt-2">
                      <p className="font-bold">Declaration:</p>
                      <p className="mt-1">
                        This is a system-generated invoice and does not require a physical signature.
                        We declare that all particulars are true and correct as per GST law.
                      </p>
                    </div>
                  </div>

                  {taxInvoiceData.settlementRowCount === 0 && (
                    <p className="mt-6 text-amber-700 text-xs">
                      No PhonePe settlement rows with settlement date in this month; values are zero.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeView === "statewise" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={statewiseStartDate}
                    onChange={(e) => setStatewiseStartDate(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={statewiseEndDate}
                    onChange={(e) => setStatewiseEndDate(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleStatewiseApply}
                  disabled={statewiseLoading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-60"
                >
                  {statewiseLoading ? "Loading…" : "Apply"}
                </button>
                <button
                  type="button"
                  onClick={handleStatewiseClear}
                  className="bg-gray-100 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-200"
                >
                  Clear
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              State is taken from each ride&apos;s <span className="font-medium">driver pickup address</span> (e.g.
              Telangana, Tamil Nadu). To match Tax Invoice preview, settlement <span className="font-medium">net amount</span>
              is treated as taxable value and IGST is computed as 18% of taxable value.
            </p>
          </div>

          {statewiseLoading && <Loader />}

          {statewiseApplied && statewiseData && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Statewise settlement (PhonePe net amount)
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {statewiseApplied.startDate} to {statewiseApplied.endDate} ·{" "}
                    {statewiseData.settlementRowCount ?? 0} settlement row(s)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleStatewiseExportCsv}
                  disabled={!statewiseData.rows || statewiseData.rows.length === 0}
                  className="inline-flex items-center justify-center gap-2 shrink-0 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  <FiDownload className="inline" />
                  Export Report
                </button>
              </div>
              <div className="overflow-x-auto p-6">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">
                        State
                      </th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700 border-b">
                        Taxable Value (Rs.)
                      </th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700 border-b">
                        IGST @18% (Rs.)
                      </th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700 border-b">
                        Total (Rs.)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {(statewiseData.rows || []).map((row) => (
                      <tr key={row.state} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900">{formatStateLabel(row.state) || "N/A"}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatInr2(row.taxableExGst)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {formatInr2(row.gstAmount)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatInr2(row.paidAmount)}</td>
                      </tr>
                    ))}
                    {statewiseData.totals && (statewiseData.rows || []).length > 0 && (
                      <tr className="bg-gray-100 font-semibold">
                        <td className="px-4 py-3 text-gray-900">Total</td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {formatInr2(statewiseData.totals.taxableExGst)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">                          
                          {formatInr2(statewiseData.totals.gstAmount)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">                          
                          {formatInr2(statewiseData.totals.paidAmount)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {(!statewiseData.rows || statewiseData.rows.length === 0) && (
                  <p className="text-sm text-amber-700 mt-4">
                    No settlement rows in this date range, or no rides linked to settlements.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommissionReport;
